import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import { string, arrayOf, func } from 'prop-types';
import { cloudinarySmartThumbUrl } from '../lib/cloudinary';
import {
  MoviesContainer,
  MoviesControls,
  MoviesControlButton,
  MoviesPoster,
  MoviesRow,
  MoviesRowWrapper,
  MoviesTitle,
  MoviesVideo,
} from './Movies.styles';

const canPlayNativeHls = () => {
  const video = document.createElement('video');
  return Boolean(video.canPlayType('application/vnd.apple.mpegurl'));
};

const getCardDimensions = (cardStyle) => (cardStyle === 'portrait'
  ? { width: 420, height: 630 }
  : { width: 640, height: 360 });

const getThumbGravity = (media) => {
  if (media.thumbnailGravity) return media.thumbnailGravity;
  if (media.useCustomGravity) return 'g_custom';
  return 'g_auto:faces';
};

function SmartPreviewVideo({
  src,
  poster,
  movieId,
  autoPlay = false,
  loop = false,
  preload = 'metadata',
}) {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !src) return undefined;

    let hls;
    const playIfNeeded = () => {
      if (!autoPlay) return;
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    };
    const isHlsSource = src.includes('.m3u8');
    if (isHlsSource && !canPlayNativeHls() && Hls.isSupported()) {
      hls = new Hls({
        enableWorker: true,
        lowLatencyMode: true,
      });
      hls.loadSource(src);
      hls.attachMedia(video);
      hls.on(Hls.Events.MANIFEST_PARSED, playIfNeeded);
    } else {
      video.src = src;
      video.load();
      playIfNeeded();
    }

    return () => {
      if (hls) hls.destroy();
    };
  }, [src, autoPlay]);

  return (
    <MoviesVideo
      ref={videoRef}
      poster={poster || undefined}
      muted
      playsInline
      loop={loop}
      autoPlay={autoPlay}
      preload={preload}
      data-movie-id={movieId}
    />
  );
}

const scrollBy = (row, direction) => {
  if (!row) return;
  const { scrollLeft, scrollWidth, clientWidth } = row;
  const offset = clientWidth * 0.8;
  const next = direction === 'left' ? scrollLeft - offset : scrollLeft + offset;
  if (direction === 'right' && scrollLeft + clientWidth >= scrollWidth - 4) {
    row.scrollTo({ left: 0, behavior: 'smooth' });
    return;
  }
  if (direction === 'left' && scrollLeft <= 4) {
    row.scrollTo({ left: scrollWidth, behavior: 'smooth' });
    return;
  }
  row.scrollTo({ left: next, behavior: 'smooth' });
};

function Movies({ title, movies, onSelect, cardStyle }) {
  const [hoveredId, setHoveredId] = useState(null);
  const [showControls, setShowControls] = useState(false);
  const rowRef = useRef(null);
  const lastAutoScroll = useRef(0);

  useEffect(() => {
    const row = rowRef.current;
    if (!row) return;
    const videos = row.querySelectorAll('video');
    videos.forEach((video) => {
      const shouldPlay = hoveredId && video.dataset.movieId === hoveredId;
      if (!shouldPlay) {
        video.pause();
        video.currentTime = 0;
      }
    });
  }, [hoveredId]);

  useEffect(() => {
    const row = rowRef.current;
    if (!row) return undefined;

    const updateControls = () => {
      setShowControls(row.scrollWidth - row.clientWidth > 4);
    };

    updateControls();
    window.addEventListener('resize', updateControls);

    let observer;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(updateControls);
      observer.observe(row);
    }

    return () => {
      window.removeEventListener('resize', updateControls);
      if (observer) observer.disconnect();
    };
  }, [movies, cardStyle]);

  const handleRowHoverScroll = (event) => {
    const row = rowRef.current;
    if (!row) return;
    const now = Date.now();
    if (now - lastAutoScroll.current < 35) return;
    const rect = row.getBoundingClientRect();
    const threshold = 48;
    if (event.clientX < rect.left + threshold) {
      row.scrollBy({ left: -28, behavior: 'auto' });
      lastAutoScroll.current = now;
    } else if (event.clientX > rect.right - threshold) {
      row.scrollBy({ left: 28, behavior: 'auto' });
      lastAutoScroll.current = now;
    }
  };

  return (
    <MoviesContainer>
      <MoviesTitle>{title}</MoviesTitle>
      <MoviesRowWrapper
        onMouseMove={handleRowHoverScroll}
        onMouseLeave={() => setHoveredId(null)}
      >
        {showControls ? (
          <MoviesControls>
            <MoviesControlButton
              type="button"
              onClick={() => scrollBy(rowRef.current, 'left')}
              aria-label="Scroll left"
            >
              &#8249;
            </MoviesControlButton>
            <MoviesControlButton
              type="button"
              onClick={() => scrollBy(rowRef.current, 'right')}
              aria-label="Scroll right"
            >
              &#8250;
            </MoviesControlButton>
          </MoviesControls>
        ) : null}
        <MoviesRow ref={rowRef}>
          {movies.map((movie, idx) => {
            const previewVideoPath = movie.previewVideoPath || movie.videoPath;
            const dimensions = getCardDimensions(cardStyle);
            const gravity = getThumbGravity(movie);
            const rawPosterPath = movie.videoPosterPath || movie.imagePath;
            const baseCardPath = movie.thumbnailOverridePath || movie.imagePath || movie.videoPosterPath;
            const posterPath = cloudinarySmartThumbUrl(rawPosterPath, {
              width: dimensions.width,
              height: dimensions.height,
              gravity,
            });
            const cardImagePath = cloudinarySmartThumbUrl(baseCardPath, {
              width: dimensions.width,
              height: dimensions.height,
              gravity,
            });
            const canShowHoverPreview = Boolean(
              previewVideoPath && !movie.disableHoverPreview,
            );

            return (
              <MoviesPoster
                key={movie.id}
                $variant={cardStyle}
                onMouseEnter={() => {
                  setHoveredId(movie.id);
                }}
                onMouseLeave={() => {
                  setHoveredId(null);
                }}
                onClick={() => onSelect(movie)}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') onSelect(movie);
                }}
              >
                {canShowHoverPreview && hoveredId === movie.id ? (
                  <SmartPreviewVideo
                    src={previewVideoPath}
                    poster={posterPath}
                    movieId={movie.id}
                    autoPlay
                    loop
                    preload="auto"
                  />
                ) : (movie.imagePath || movie.videoPosterPath) ? (
                  <img
                    src={cardImagePath}
                    alt={movie.title}
                    loading="lazy"
                  />
                ) : movie.videoPath ? (
                  <SmartPreviewVideo
                    src={previewVideoPath}
                    poster={posterPath}
                    movieId={movie.id}
                    preload="metadata"
                  />
                ) : null}
                {canShowHoverPreview && !previewVideoPath.includes('.m3u8') ? (
                  <video
                    src={previewVideoPath}
                    muted
                    playsInline
                    preload={idx < 8 ? 'auto' : 'metadata'}
                    style={{ display: 'none' }}
                  />
                ) : null}
              </MoviesPoster>
            );
          })}
        </MoviesRow>
      </MoviesRowWrapper>
    </MoviesContainer>
  );
}

export default Movies;

Movies.propTypes = {
  title: string,
  movies: arrayOf,
  onSelect: func,
  cardStyle: string,
}.isRequired;
